/** Open pipeline order (excludes standby and terminal outcomes). */
export const CANDIDATE_PIPELINE_OPEN_ORDER = [
  'processing',
  'need_contact',
  'interview_invited',
  'unpaid_training',
  'paid_probation_us',
  'approved_for_hiring'
];

/** Status codes that represent an active open recruitment pipeline. */
export const OPEN_PIPELINE_STATUSES = [...CANDIDATE_PIPELINE_OPEN_ORDER];

/** Linear next status for Next stage (detail page). No next for approved_for_hiring. */
export const NEXT_PIPELINE_STATUS = Object.fromEntries(
  CANDIDATE_PIPELINE_OPEN_ORDER.slice(0, -1).map((code, index) => [
    code,
    CANDIDATE_PIPELINE_OPEN_ORDER[index + 1]
  ])
);

/** Short stepper labels (UI copy; backend codes stay unchanged). */
export const PIPELINE_STEP_UI_LABELS = {
  processing: 'Review',
  need_contact: 'Contact',
  interview_invited: 'Interview',
  unpaid_training: 'Training',
  paid_probation_us: 'Probation',
  approved_for_hiring: 'Ready to hire'
};

export function getNextPipelineStatus(currentStatus) {
  const code = String(currentStatus || '');
  return NEXT_PIPELINE_STATUS[code] || null;
}

export function canAdvanceCandidate(candidate) {
  if (!candidate || candidate.deleted_at || candidate.is_deleted) return false;
  if (candidate.status === 'hired') return false;
  return Boolean(getNextPipelineStatus(candidate.status));
}

const CANDIDATE_STATUS_LIFECYCLE_META = {
  processing: { lifecycle: 'open', pipelineStep: 1, isClosed: false },
  need_contact: { lifecycle: 'open', pipelineStep: 2, isClosed: false },
  interview_invited: { lifecycle: 'open', pipelineStep: 3, isClosed: false },
  unpaid_training: { lifecycle: 'open', pipelineStep: 4, isClosed: false },
  paid_probation_us: { lifecycle: 'open', pipelineStep: 5, isClosed: false },
  approved_for_hiring: { lifecycle: 'open', pipelineStep: 6, isClosed: false },
  hired: { lifecycle: 'successful', pipelineStep: null, isClosed: false },
  declined: { lifecycle: 'closed', pipelineStep: null, isClosed: true },
  offer_declined: { lifecycle: 'closed', pipelineStep: null, isClosed: true },
  no_reply: { lifecycle: 'closed', pipelineStep: null, isClosed: true },
  failed: { lifecycle: 'closed', pipelineStep: null, isClosed: true },
  standby: { lifecycle: 'closed', pipelineStep: null, isClosed: true }
};

export const CANDIDATE_STATUSES = [
  { code: 'processing', label: 'Processing', group: 'active' },
  { code: 'need_contact', label: 'Need to contact', group: 'active' },
  { code: 'interview_invited', label: 'Invited for Interview', group: 'interview' },
  { code: 'unpaid_training', label: 'Pre-Approved for Unpaid Training', group: 'training' },
  { code: 'paid_probation_us', label: 'Paid Probation Period (USA)', group: 'training' },
  { code: 'approved_for_hiring', label: 'Approved for Hiring', group: 'approved' },
  { code: 'declined', label: 'Declined', group: 'closed' },
  { code: 'standby', label: 'Standby', group: 'on_hold' },
  { code: 'offer_declined', label: 'Declined offer', group: 'closed' },
  { code: 'no_reply', label: 'No reply', group: 'closed' },
  { code: 'failed', label: 'Failed', group: 'closed' },
  { code: 'hired', label: 'Hired', group: 'hired' }
];

export const CANDIDATE_SOURCES = [
  { code: 'facebook', label: 'Facebook' },
  { code: 'instagram', label: 'Instagram' },
  { code: 'youtube', label: 'Youtube' },
  { code: 'rabota_md', label: 'Rabota.md' },
  { code: 'referral_program', label: 'Referral program' },
  { code: 'tiktok', label: 'Tik Tok' },
  { code: 'other_social_media', label: 'Other social media' },
  { code: 'recommendations', label: 'Recommendations' },
  { code: 'google_web_ads', label: 'Google Web Ads' },
  { code: 'indeed', label: 'Indeed' },
  { code: 'other', label: 'Other' }
];

export const STATUS_SUMMARY_GROUPS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active', statuses: ['processing', 'need_contact'] },
  { key: 'interview', label: 'Interview', statuses: ['interview_invited'] },
  { key: 'training', label: 'Training', statuses: ['unpaid_training', 'paid_probation_us'] },
  { key: 'approved', label: 'Approved', statuses: ['approved_for_hiring'] },
  { key: 'on_hold', label: 'On hold', statuses: ['standby'] },
  { key: 'hired', label: 'Hired', statuses: ['hired'] },
  { key: 'closed', label: 'Closed', statuses: ['declined', 'offer_declined', 'failed', 'no_reply'] }
];

export const CANDIDATE_STATUS_SHORT_LABELS = {
  processing: 'Initial review',
  need_contact: 'Contact',
  interview_invited: 'Interview',
  unpaid_training: 'Training',
  paid_probation_us: 'Probation',
  approved_for_hiring: 'Approved',
  declined: 'Declined',
  standby: 'Standby',
  offer_declined: 'Offer declined',
  no_reply: 'No reply',
  failed: 'Failed',
  hired: 'Hired'
};

export const CANDIDATE_STATUS_SEMANTIC_TONE = {
  processing: 'info',
  need_contact: 'neutral',
  interview_invited: 'info',
  unpaid_training: 'warning',
  paid_probation_us: 'warning',
  approved_for_hiring: 'success',
  hired: 'success',
  declined: 'danger',
  standby: 'neutral',
  offer_declined: 'danger',
  no_reply: 'neutral',
  failed: 'danger'
};

export const REASON_CODES = {
  declined: [
    { code: 'insufficient_experience', label: 'Insufficient experience' },
    { code: 'skills_mismatch', label: 'Skills mismatch' },
    { code: 'salary_expectations', label: 'Salary expectations' },
    { code: 'location_mismatch', label: 'Location mismatch' },
    { code: 'position_closed', label: 'Position closed' },
    { code: 'other', label: 'Other' }
  ],
  offer_declined: [
    { code: 'accepted_other_offer', label: 'Accepted other offer' },
    { code: 'salary', label: 'Salary' },
    { code: 'schedule', label: 'Schedule' },
    { code: 'location', label: 'Location' },
    { code: 'personal_reason', label: 'Personal reason' },
    { code: 'no_reason_given', label: 'No reason given' },
    { code: 'other', label: 'Other' }
  ],
  failed: [
    { code: 'failed_interview', label: 'Failed interview' },
    { code: 'failed_training', label: 'Failed training' },
    { code: 'failed_probation', label: 'Failed probation' },
    { code: 'background_check', label: 'Background check' },
    { code: 'documents', label: 'Documents' },
    { code: 'other', label: 'Other' }
  ],
  standby: [
    { code: 'no_open_position', label: 'No open position' },
    { code: 'future_candidate', label: 'Future candidate' },
    { code: 'awaiting_decision', label: 'Awaiting decision' },
    { code: 'other', label: 'Other' }
  ]
};

/** @deprecated Use fetchCandidateOptions() for transition rules — display-only mirror. */
export const STATUS_TRANSITIONS = {
  processing: ['need_contact', 'interview_invited', 'standby', 'declined', 'no_reply'],
  need_contact: ['interview_invited', 'standby', 'declined', 'no_reply'],
  interview_invited: [
    'unpaid_training',
    'paid_probation_us',
    'approved_for_hiring',
    'failed',
    'declined',
    'no_reply',
    'standby'
  ],
  unpaid_training: ['paid_probation_us', 'approved_for_hiring', 'failed', 'declined', 'standby'],
  paid_probation_us: ['approved_for_hiring', 'failed', 'declined', 'standby'],
  approved_for_hiring: ['declined', 'offer_declined', 'standby'],
  declined: ['standby'],
  offer_declined: ['standby'],
  failed: ['standby'],
  no_reply: ['need_contact', 'standby'],
  standby: ['need_contact', 'interview_invited', 'declined'],
  hired: []
};

export const PERIOD_STATUSES = ['unpaid_training', 'paid_probation_us'];

export const STATUSES_REQUIRING_REASON = ['declined', 'offer_declined', 'failed', 'standby'];

/** All statuses that can be set via create/update (excludes hired). */
export const ASSIGNABLE_STATUSES = CANDIDATE_STATUSES.filter((s) => s.code !== 'hired').map(
  (s) => s.code
);

export const CANDIDATE_STATUS_CHART_COLORS = {
  processing: '#3b82f6',
  need_contact: '#64748b',
  interview_invited: '#0ea5e9',
  unpaid_training: '#d97706',
  paid_probation_us: '#ca8a04',
  approved_for_hiring: '#16a34a',
  declined: '#ef4444',
  standby: '#94a3b8',
  offer_declined: '#dc2626',
  no_reply: '#6b7280',
  failed: '#991b1b',
  hired: '#15803d'
};

export function getStatusLifecycleMeta(statusCode) {
  if (!statusCode) {
    return { lifecycle: '', pipelineStep: null, isClosed: false };
  }
  return (
    CANDIDATE_STATUS_LIFECYCLE_META[statusCode] ?? {
      lifecycle: '',
      pipelineStep: null,
      isClosed: false
    }
  );
}

export function isOpenStatus(statusCode) {
  return getStatusLifecycleMeta(statusCode).lifecycle === 'open';
}

export function getGroupForStatus(statusCode) {
  if (!statusCode) return '';
  const group = STATUS_SUMMARY_GROUPS.find(
    (g) => g.key !== 'all' && g.statuses?.includes(statusCode)
  );
  return group?.key ?? '';
}

/** Map filter state to KPI card key. */
export function resolveActiveSummaryGroup({ status = '', status_group = '', scope = '' } = {}) {
  if (scope === 'interviews_week') return 'interviews_week';
  if (scope === 'hired_this_month') return 'hired';
  return status ? getGroupForStatus(status) || 'all' : status_group || 'all';
}

export function doesStatusBelongToGroup(statusCode, groupKey) {
  if (!groupKey || groupKey === 'all') return true;
  const group = STATUS_SUMMARY_GROUPS.find((g) => g.key === groupKey);
  return group?.statuses?.includes(statusCode) ?? false;
}

export function getStatusLabel(code) {
  return CANDIDATE_STATUSES.find((s) => s.code === code)?.label || code;
}

export function getStatusShortLabel(code) {
  return CANDIDATE_STATUS_SHORT_LABELS[code] || getStatusLabel(code);
}

export function getReasonLabel(statusCode, reasonCode) {
  if (!reasonCode) return '';
  const list = REASON_CODES[statusCode] || [];
  return list.find((r) => r.code === reasonCode)?.label || reasonCode;
}

export function getAllowedNextStatusCodes(currentStatus) {
  if (!currentStatus || currentStatus === 'hired') {
    return currentStatus ? [currentStatus] : [];
  }
  const next = STATUS_TRANSITIONS[currentStatus] || [];
  return [currentStatus, ...next];
}

/** Dropdown options for mock-simple free status assignment (all except hired). */
export function getAssignableStatusOptions() {
  return CANDIDATE_STATUSES.filter((s) => s.code !== 'hired').map((s) => ({
    label: s.label,
    value: s.code
  }));
}

/**
 * Status select options. Prefer free assignment (all assignable) for mock-simple UX.
 * Pass `freeAssignment: false` to restrict to graph transitions.
 */
export function getStatusSelectOptions(currentStatus, { freeAssignment = true } = {}) {
  if (currentStatus === 'hired') {
    return [{ label: getStatusLabel('hired'), value: 'hired' }];
  }
  if (freeAssignment) {
    return getAssignableStatusOptions();
  }
  const allowed = new Set(getAllowedNextStatusCodes(currentStatus));
  return CANDIDATE_STATUSES.filter((s) => allowed.has(s.code)).map((s) => ({
    label: s.label,
    value: s.code
  }));
}

export function buildStatusCountSummary(byStatus = {}) {
  const all = Object.values(byStatus).reduce((sum, n) => sum + (Number(n) || 0), 0);
  const groups = {};
  for (const group of STATUS_SUMMARY_GROUPS) {
    if (group.key === 'all') continue;
    groups[group.key] = (group.statuses || []).reduce(
      (sum, code) => sum + (Number(byStatus[code]) || 0),
      0
    );
  }
  return { all, groups, by_status: byStatus };
}

/** KPI cards shown on list page. */
export const KPI_SUMMARY_CARDS = [
  { key: 'all', label: 'Total candidates', tone: 'success', showPercent: false },
  { key: 'active', label: 'Active pipeline', tone: 'success', showPercent: true },
  {
    key: 'interviews_week',
    label: 'Interviews this week',
    tone: 'info',
    showPercent: false,
    isScopeFilter: true
  },
  { key: 'hired', label: 'Hired this month', tone: 'purple', showPercent: false }
];
